/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import * as logger from '@ion/core/src/logger/logger';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {
  formatReconnectionAttempts,
  formatSubscriptionResults
} from './helpers';
import {
  EntitySubscription,
  ObservableEntityId,
  SocketConnection,
  SubscriptionResults
} from './types';

/**
 * Defines the log levels supported by the Socket logging facility; a narrowed
 * subset of the levels provided by the underlying iOn Logging API.
 */
type SocketLogLevel = Extract<logger.LogLevel, 'info' | 'warn' | 'error'>;

/**
 * Provides the logger for outputting a pre-handshake token retrieval success.
 */
export const preauthSucceeded = () => {
  log('Pre-Auth Single Use Token Successfully Retrieved');
};

/**
 * Provides the logger for outputting a pre-handshake token retrieval failure.
 */
export const preauthFailed = (err: Error) => {
  log(`Pre-Auth Single Use Token Retrieval Failed [${err?.toString()}]`, null, 'error');
};

/**
 * Provides the logger for outputting a connection event, which is to be
 * logged when a connection is established.
 */
export const connected = (event: Event, instance: SocketConnection) => {
  log('Connection Successfully Established', instance);
};

/**
 * Provides the logger for outputting a connection close event, which is to be
 * logged when a connection is closed (either explicitly or implicitly).
 */
export const closed = (event: CloseEvent | {reason: string}, instance: SocketConnection) => {
  log(`Connection Closed [${event?.reason || 'N/A'}]`, instance);
};

/**
 * Provides the logger for outputting the results of a subscription invocation,
 * whereby entity type, subscription results, and failures are logged.
 */
export const subscription = (results: SubscriptionResults, isSubscription: boolean, socket: SocketConnection) => {
  log(formatSubscriptionResults(results, isSubscription), socket);
};

/**
 * Provides the logger for outputting a data event, which is to be logged when a
 * message is received.
 */
export const data = (event: MessageEvent, instance: SocketConnection) => {
  log('Message Received', instance);
};

/**
 * Provides the logger for outputting a connection error event, which is to be
 * logged when a connection fails.
 */
export const error = (event: ErrorEvent | {message: string}, instance: SocketConnection) => {
  log(`Connection Failure: [${event.message}]`, instance, 'error');
};

/**
 * Provides the logger for outputting a connection error event, which is to be
 * logged when a connection fails.
 */
export const processingException = ({message}: Error, instance: SocketConnection) => {
  log(`Incoming Message Processing Failure: [${message}]`, instance, 'error');
};

/**
 * Provides the logger for outputting an unresolved entity type, which is to be
 * logged when the entity type of a received message could not be resolved, and
 * the legacy identifier only observer lookup is thus applied.
 */
export const unmappedEntity = (id: ObservableEntityId) => {
  log(`Unmapped entity change message received for id [${id}]. Falling back to the identifier only observer lookup`, null, 'warn');
};

/**
 * Provides the logger for outputting a reconnection attempt, which is to be
 * logged when a connection fails and a new connection is being established.
 */
export const reconnect = (attempts: number, instance: SocketConnection) => {
  log(`Attempting to Re-establish Connection ${formatReconnectionAttempts(attempts)} ...`, instance);
};

/**
 * Provides the logger for outputting a reconnection success, which is to be
 * logged when a reconnection attempt succeeds.
 */
export const reconnected = (attempts: number, instance: SocketConnection) => {
  log(`Connection Successfully Re-established after ${formatReconnectionAttempts(attempts, false)}`, instance);
};

/**
 * Provides the logger for outputting a subscription resynchronization, which is
 * to be logged when the subscriptions held by the service are re-established
 * from the currently registered observers; either upon the initial connection,
 * or following a reconnection.
 */
export const resynchronized = (
  entity: EntitySubscription, ids: ObservableEntityId[], instance: SocketConnection) => {
  log(`${capitalizeFirstLetter(entity)} Subscriptions Resynchronized: [${ids.toString()}]`, instance);
};

/**
 * Provides the logger for outputting a keepalive ping message response.
 */
export const keepalive = (instance: SocketConnection) => {
  log('Keep Alive Ping Successfully Executed.', instance);
};

/**
 * Provides the default logging facility through which all Socket logs are generated
 * and emitted.
 *
 * @param msg      Defines the message to be logged to the underlying logger.
 * @param instance Defines the Socket instance for which the message is to be logged,
 *                 or `null` when the message applies to no specific instance.
 * @param level    Defines the level at which the message is to be logged.
 */
const log = (msg: string, instance?: SocketConnection | null, level: SocketLogLevel = 'info') => {
  const status  = instance ? (instance?.isConnected ? 'CONNECTED' : 'DISCONNECTED') : null;
  const message = `[WebSocket API] ${msg}.${status ? ` Status: ${status}` : ''}`;
  logger[level](message);
};
